"use client";
import styles from "../../guest.module.css";

import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";

import { Button } from "@/components/atoms";
import Input from "@/components/molecules/NewForm/Input";
import { ResetPasswordStoryblok } from "@/types/storyblok-blok-types";
import { StoryblokStory } from "storyblok-generate-ts";

import Link from "@/lib/bloks/atoms/Link";
import { useResetPasswordEmail } from "@/lib/hooks/useSelection";
import { useValidation } from "@/lib/hooks/useValidation";
import { useTranslations } from "next-intl";

const defaultValues = {
  email: ""
};

interface ResetPasswordFormProps {
  pageData: StoryblokStory<ResetPasswordStoryblok>;
}

export const ResetPasswordForm = ({ pageData }: ResetPasswordFormProps) => {
  const t = useTranslations();
  const validation = useValidation();
  const { resetPassword } = useResetPasswordEmail();

  const error = resetPassword.error?.message;

  const schema = validation
    .object({
      email: validation.string().email().required()
    })
    .required();

  const form = useForm({
    defaultValues,
    resolver: yupResolver(schema)
  });

  const { handleSubmit } = form;

  const onSubmit = handleSubmit((formData) => {
    resetPassword.mutate({
      email: formData.email,
      linkUri: "/set-new-password"
    });
  });

  const contactUsLink = pageData.content.contactUs?.[0];
  const successMessage = pageData.content?.successMessage;

  return (
    <div className={styles.formWrapper}>
      <FormProvider {...form}>
        <form onSubmit={onSubmit}>
          <Input
            name="email"
            label={t("common.fields.email")}
            inputProps={{ type: "email" }}
          />

          <Button color="black" type="submit">
            {t("myAccount.resetPasswordPage.button")}
          </Button>

          {resetPassword.isSuccess && successMessage && (
            <div className="notification">{successMessage}</div>
          )}

          {error && <div className="notification">{error}</div>}

          {contactUsLink && (
            <div className={styles.linkWrapper}>
              <span>{t("myAccount.resetPasswordPage.contactUs")}</span>

              <Link blok={contactUsLink} key={contactUsLink._uid} />
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
};
